/**
 * Frontend mirror of backend/constants/permissions.js. Keep in sync.
 *
 * The authoritative `userPermissions: string[]` arrives from the backend on
 * GET /team/:teamId (see selectedTeam.userPermissions). We expose the role
 * matrix here only as a fallback when the backend payload is missing, so the
 * UI degrades gracefully (e.g. on first paint before the team fetch lands).
 */

export const PERMISSIONS = Object.freeze({
  TEAM_DELETE: 'team:delete',
  TEAM_TRANSFER_OWNERSHIP: 'team:transfer_ownership',
  MEMBERS_INVITE: 'members:invite',
  MEMBERS_REMOVE: 'members:remove',
  MEMBERS_CHANGE_ROLE: 'members:change_role',
  BILLING_VIEW: 'billing:view',
  BILLING_MANAGE: 'billing:manage',
  INTEGRATIONS_THIRDPARTY_MANAGE: 'integrations:thirdparty:manage',
  INTEGRATIONS_DEVELOPER_MANAGE: 'integrations:developer:manage',
  SETTINGS_ISSUE_THRESHOLDS_MANAGE: 'settings:issue_thresholds:manage',
  SETTINGS_USE_CASES_MANAGE: 'settings:use_cases:manage',
  CONFIG_MODEL_PRICES_MANAGE: 'config:model_prices:manage',
  APP_VIEW: 'app:view',
  APP_USE: 'app:use',
});

const P = PERMISSIONS;

export const ROLE_PERMISSIONS = Object.freeze({
  owner: [
    P.TEAM_DELETE, P.TEAM_TRANSFER_OWNERSHIP,
    P.MEMBERS_INVITE, P.MEMBERS_REMOVE, P.MEMBERS_CHANGE_ROLE,
    P.BILLING_VIEW, P.BILLING_MANAGE,
    P.INTEGRATIONS_THIRDPARTY_MANAGE, P.INTEGRATIONS_DEVELOPER_MANAGE,
    P.SETTINGS_ISSUE_THRESHOLDS_MANAGE, P.SETTINGS_USE_CASES_MANAGE,
    P.CONFIG_MODEL_PRICES_MANAGE,
    P.APP_VIEW, P.APP_USE,
  ],
  admin: [
    P.MEMBERS_INVITE, P.MEMBERS_REMOVE, P.MEMBERS_CHANGE_ROLE,
    P.BILLING_VIEW, P.BILLING_MANAGE,
    P.INTEGRATIONS_THIRDPARTY_MANAGE, P.INTEGRATIONS_DEVELOPER_MANAGE,
    P.SETTINGS_ISSUE_THRESHOLDS_MANAGE, P.SETTINGS_USE_CASES_MANAGE,
    P.CONFIG_MODEL_PRICES_MANAGE,
    P.APP_VIEW, P.APP_USE,
  ],
  developer: [
    P.INTEGRATIONS_THIRDPARTY_MANAGE, P.INTEGRATIONS_DEVELOPER_MANAGE,
    P.SETTINGS_ISSUE_THRESHOLDS_MANAGE, P.SETTINGS_USE_CASES_MANAGE,
    P.CONFIG_MODEL_PRICES_MANAGE,
    P.APP_VIEW, P.APP_USE,
  ],
  member: [P.APP_VIEW, P.APP_USE],
  billing: [P.BILLING_VIEW, P.BILLING_MANAGE],
  // Read-only: a visitor of the shared demo team (not a member). Not assignable.
  viewer: [P.APP_VIEW],
});

/** Fallback when backend payload hasn't loaded yet. */
export function getRolePermissions(role) {
  const r = String(role || '').toLowerCase();
  return ROLE_PERMISSIONS[r] ? [...ROLE_PERMISSIONS[r]] : [];
}
